// select, multi_select et status : des options définies dans le champ.
//
// La valeur stockée est l'id de l'option, jamais son nom : renommer une
// option ne réécrit aucune ligne. À l'écriture, on accepte l'id ou le nom
// (casse et accents ignorés), pour que l'agent, un CSV ou Jev puissent écrire
// « En cours » sans connaître les ids.

import { z } from "zod";
import { PILL_COLORS } from "../../lib/colorPill";
import { generateColumnId } from "../../lib/llmId";
import {
  compareText,
  fail,
  foldText,
  ok,
  type FieldKind,
  type FilterOperator,
} from "../kind";
import type { CoerceResult, FieldSettings, FieldSettingsMap } from "../types";
import { scalarToText } from "./text";

const optionSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().trim().min(1).max(100),
  // La palette des pastilles de l'app (cf. convex/lib/colorPill.ts).
  color: z.enum(PILL_COLORS).default("default"),
  // Pour le LLM : ce que l'option veut dire (consigne de classification).
  description: z.string().max(500).optional(),
});

export type ChoiceOption = z.infer<typeof optionSchema>;

/** Noms uniques à la casse et aux accents près, ids uniques. */
function uniqueOptions<T extends { id: string; name: string }>(options: T[]): boolean {
  const ids = new Set(options.map((option) => option.id));
  const names = new Set(options.map((option) => foldText(option.name)));
  return ids.size === options.length && names.size === options.length;
}

const optionsSchema = z
  .array(optionSchema)
  .max(500)
  .default([])
  .refine(uniqueOptions, "Option names and ids must be unique.");

const commonChoiceSettings = {
  options: optionsSchema,
  // Ordre d'affichage du menu et des groupes du kanban.
  sort: z.enum(["manual", "alphabetical"]).default("manual"),
  // Écrire un nom inconnu crée l'option (sinon : refus).
  allowCreate: z.boolean().default(true),
  display: z.enum(["pill", "text"]).default("pill"),
};

export const selectSettings = z.object(commonChoiceSettings);

export const multiSelectSettings = z.object({
  ...commonChoiceSettings,
  maxSelections: z.number().int().min(1).max(100).optional(),
});

export const STATUS_GROUPS = ["todo", "in_progress", "done"] as const;

/** Les types à options. */
export function isChoiceType(type: string): type is "select" | "multi_select" | "status" {
  return type === "select" || type === "multi_select" || type === "status";
}
export type StatusGroup = (typeof STATUS_GROUPS)[number];

const statusOptionSchema = optionSchema.extend({
  group: z.enum(STATUS_GROUPS),
});

export const statusSettings = z.object({
  options: z
    .array(statusOptionSchema)
    .max(100)
    .default([
      { id: "status_todo", name: "To do", color: "grey", group: "todo" },
      { id: "status_doing", name: "In progress", color: "blue", group: "in_progress" },
      { id: "status_done", name: "Done", color: "green", group: "done" },
    ])
    .refine(uniqueOptions, "Option names and ids must be unique."),
  // Libellés des trois groupes, renommables.
  groupLabels: z
    .object({
      todo: z.string().max(40).optional(),
      in_progress: z.string().max(40).optional(),
      done: z.string().max(40).optional(),
    })
    .default({}),
  display: z.enum(["pill", "checkbox"]).default("pill"),
});

type AnyChoiceSettings = { options: ChoiceOption[]; sort?: "manual" | "alphabetical" };

/** Un id d'option nouveau, absent de la liste. */
export function newOptionId(options: { id: string }[]): string {
  const taken = new Set(options.map((option) => option.id));
  for (;;) {
    const id = `opt_${generateColumnId()}`;
    if (!taken.has(id)) return id;
  }
}

/**
 * Une option nommée `name` ajoutée aux réglages d'un champ à options (un
 * statut neuf va dans « todo »), et son id ; `null` pour un champ sans options.
 */
export function withNewOption(
  field: { type: string; settings: FieldSettings },
  name: string,
): { id: string; settings: FieldSettings } | null {
  if (!("options" in field.settings)) return null;
  const options = field.settings.options;
  const id = newOptionId(options);
  const option = { id, name, color: "default" as const };
  return {
    id,
    settings: {
      ...field.settings,
      options: [...options, field.type === "status" ? { ...option, group: "todo" as const } : option],
    } as FieldSettings,
  };
}

/** Les options dans leur ordre d'affichage. */
export function orderedOptions<O extends ChoiceOption>(settings: {
  options: O[];
  sort?: "manual" | "alphabetical";
}): O[] {
  return settings.sort === "alphabetical"
    ? [...settings.options].sort((a, b) => compareText(a.name, b.name))
    : settings.options;
}

/** Id ou nom → option ; `null` si inconnue. */
export function findOption<O extends ChoiceOption>(options: O[], raw: string): O | null {
  const exact = options.find((option) => option.id === raw);
  if (exact) return exact;
  const folded = foldText(raw.trim());
  return options.find((option) => foldText(option.name) === folded) ?? null;
}

function optionName(settings: AnyChoiceSettings, id: string): string {
  return settings.options.find((option) => option.id === id)?.name ?? "";
}

function optionRank(settings: AnyChoiceSettings, id: string): number {
  const index = orderedOptions(settings).findIndex((option) => option.id === id);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

/** Une ou plusieurs valeurs brutes : tableau, ou « a, b ; c ». */
function rawItems(input: unknown, split: boolean): string[] | null {
  if (Array.isArray(input)) {
    const items = input.map((item) => scalarToText(item));
    return items.every((item): item is string => item !== null)
      ? items.map((item) => item.trim()).filter((item) => item !== "")
      : null;
  }
  const text = scalarToText(input);
  if (text === null) return null;
  const items = split ? text.split(/[,;\n]/) : [text];
  return items.map((item) => item.trim()).filter((item) => item !== "");
}

function resolveOne<T extends "select" | "status">(
  settings: AnyChoiceSettings,
  input: unknown,
): CoerceResult<T> {
  const items = rawItems(input, false);
  if (items === null) return fail("Expected an option.");
  if (items.length === 0) return ok(null);
  if (items.length > 1) return fail("Only one option can be selected.");
  const option = findOption(settings.options, items[0]!);
  if (!option) {
    return fail(`Unknown option "${items[0]}".`, { unknownOptions: [items[0]!] });
  }
  return ok(option.id);
}

function singleChoiceOperators<T extends "select" | "status">(): FilterOperator<T, string[]>[] {
  return [
    { id: "is_any_of", operand: "values", test: (v, o) => o.includes(v as string) },
    {
      id: "is_none_of",
      operand: "values",
      matchesEmpty: true,
      test: (v, o) => !o.includes(v as string),
    },
  ];
}

const CHOICE_AGGREGATES = [
  "count",
  "count_empty",
  "count_not_empty",
  "count_unique",
  "percent_empty",
  "percent_not_empty",
] as const;

export const selectKind: FieldKind<"select"> = {
  type: "select",
  settings: selectSettings,
  value: z.string().min(1),
  coerce: (input, settings) => resolveOne(settings, input),
  format: (value, settings) => optionName(settings, value),
  compare: (a, b, settings) => optionRank(settings, a) - optionRank(settings, b),
  filters: singleChoiceOperators<"select">(),
  aggregates: [...CHOICE_AGGREGATES],
  groupKeys: (value) => [value],
  groups: (settings) =>
    orderedOptions(settings).map((option) => ({ key: option.id, label: option.name })),
};

export const multiSelectKind: FieldKind<"multi_select"> = {
  type: "multi_select",
  settings: multiSelectSettings,
  value: z.array(z.string().min(1)).min(1).max(100),
  coerce(input, settings) {
    const items = rawItems(input, true);
    if (items === null) return fail("Expected a list of options.");
    const ids: string[] = [];
    const unknown: string[] = [];
    for (const item of items) {
      const option = findOption(settings.options, item);
      if (!option) unknown.push(item);
      else if (!ids.includes(option.id)) ids.push(option.id);
    }
    if (unknown.length > 0) {
      return fail(`Unknown options: ${unknown.map((name) => `"${name}"`).join(", ")}.`, {
        unknownOptions: unknown,
      });
    }
    if (ids.length === 0) return ok(null);
    if (settings.maxSelections !== undefined && ids.length > settings.maxSelections) {
      return fail(`At most ${settings.maxSelections} options can be selected.`);
    }
    return ok(ids);
  },
  isEmpty: (value) => value.length === 0,
  format: (value, settings) =>
    value
      .map((id) => optionName(settings, id))
      .filter((name) => name !== "")
      .join(", "),
  compare: (a, b, settings) => {
    const rank = (ids: string[]) => Math.min(...ids.map((id) => optionRank(settings, id)));
    return rank(a) - rank(b) || a.length - b.length;
  },
  filters: [
    { id: "is_any_of", operand: "values", test: (v, o: string[]) => v.some((id) => o.includes(id)) },
    {
      id: "is_none_of",
      operand: "values",
      matchesEmpty: true,
      test: (v, o: string[]) => !v.some((id) => o.includes(id)),
    },
    { id: "has_all_of", operand: "values", test: (v, o: string[]) => o.every((id) => v.includes(id)) },
  ],
  aggregates: [...CHOICE_AGGREGATES],
  groupKeys: (value) => value,
  groups: (settings) =>
    orderedOptions(settings).map((option) => ({ key: option.id, label: option.name })),
};

const DEFAULT_GROUP_LABELS: Record<StatusGroup, string> = {
  todo: "To do",
  in_progress: "In progress",
  done: "Done",
};

export function statusGroupOf(
  settings: FieldSettingsMap["status"],
  optionId: string,
): StatusGroup | null {
  return settings.options.find((option) => option.id === optionId)?.group ?? null;
}

export const statusKind: FieldKind<"status"> = {
  type: "status",
  settings: statusSettings,
  value: z.string().min(1),
  coerce: (input, settings) => resolveOne(settings, input),
  format: (value, settings) => optionName(settings, value),
  // Par groupe d'abord (à faire, en cours, terminé), puis par option.
  compare: (a, b, settings) => {
    const groupRank = (id: string) => {
      const group = statusGroupOf(settings, id);
      return group ? STATUS_GROUPS.indexOf(group) : STATUS_GROUPS.length;
    };
    return groupRank(a) - groupRank(b) || optionRank(settings, a) - optionRank(settings, b);
  },
  filters: [
    ...singleChoiceOperators<"status">(),
    { id: "is_done", operand: "none", test: (v, _o, s) => statusGroupOf(s, v) === "done" },
    {
      id: "is_not_done",
      operand: "none",
      matchesEmpty: true,
      test: (v, _o, s) => statusGroupOf(s, v) !== "done",
    },
  ],
  aggregates: [...CHOICE_AGGREGATES],
  groupKeys: (value) => [value],
  groups: (settings) =>
    STATUS_GROUPS.flatMap((group) =>
      settings.options
        .filter((option) => option.group === group)
        .map((option) => ({ key: option.id, label: option.name })),
    ),
};

export function statusGroupLabel(settings: FieldSettingsMap["status"], group: StatusGroup): string {
  return settings.groupLabels[group] ?? DEFAULT_GROUP_LABELS[group];
}
